import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import RoleShell from '../../components/Enrollment/RoleShell';
import { Prod01 } from '../../constants/ActionType';
import { fetchMyOrganizations } from '../../utils/useMyOrganizations';
import { useSortableTable, SortableTh, nameFromString } from '../../components/SortableTable';
import { STATUS_STYLE, MonthPicker, ClaimCell } from '../Accounting/AccountingMonthly';

// Billing — Suggested codes: the biller's worklist. Same server rows as the Accounting summary
// (/accounting/monthly, Batch 2 programBilling determination) shown code-by-code with reasons and blockers.
const STATUS_WORD = { ready: 'Ready', almost: 'Almost', blocked: 'Blocked', not_billable: 'Not billable' };
const FILTERS = [['all', 'All'], ['ready', 'Ready'], ['almost', 'Almost'], ['blocked', 'Blocked'], ['not_billable', 'Not billable']];
const PROFILE_ROLES = ['admin', 'adminStaff'];
const errText = (e) => e?.response?.data?.message || e?.message || 'Request failed';

const SORT_COLUMNS = {
    patient: { type: 'name', value: (r) => nameFromString(r?.patientName) },
    practice: { type: 'string', value: (r) => r?.practice },
    program: { type: 'string', value: (r) => r?.programLabel },
    status: { type: 'string', value: (r) => STATUS_WORD[r?.status] || r?.status },
    claim: { type: 'string', value: (r) => r?.claim?.label },
};

const Status = ({ row }) => {
    const s = STATUS_STYLE[row.status] || STATUS_STYLE.not_billable;
    return (
        <span data-testid="bc-status" title={row.statusLabel} style={{ background: s.bg, color: s.fg, borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
            {STATUS_WORD[row.status] || row.statusLabel}
        </span>
    );
};

// Accessible detail tip: opens on hover, keyboard focus and tap; role=tooltip linked by aria-describedby; Esc / outside tap closes.
const TIP_STYLE = {
    position: 'absolute', zIndex: 1070, top: '100%', left: 0, marginTop: 4, minWidth: 220, maxWidth: 320, padding: '6px 8px',
    background: '#212529', color: '#fff', borderRadius: 4, fontSize: 12, fontWeight: 400, lineHeight: 1.4, whiteSpace: 'normal', textAlign: 'left',
    boxShadow: '0 2px 6px rgba(0,0,0,.25)',
};
const TRIGGER_STYLE = { font: 'inherit', color: 'inherit', background: 'none', border: 0, padding: 0, cursor: 'help', whiteSpace: 'nowrap', textAlign: 'left' };
export const Tip = ({ tip, children, triggerStyle, triggerClassName, testId, ariaLabel }) => {
    const id = useId();
    const ref = useRef(null);
    const [hover, setHover] = useState(false);
    const [focus, setFocus] = useState(false);
    const [pinned, setPinned] = useState(false);
    const open = hover || focus || pinned;
    useEffect(() => {
        if (!pinned) return undefined;
        const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setPinned(false); };
        document.addEventListener('pointerdown', away);
        document.addEventListener('touchstart', away);
        return () => { document.removeEventListener('pointerdown', away); document.removeEventListener('touchstart', away); };
    }, [pinned]);
    const close = () => { setHover(false); setFocus(false); setPinned(false); };
    return (
        <span ref={ref} style={{ position: 'relative', display: 'inline-block' }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
            <button
                type="button"
                className={triggerClassName}
                style={{ ...TRIGGER_STYLE, ...triggerStyle }}
                aria-describedby={id}
                aria-label={ariaLabel}
                data-testid={testId}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                onClick={() => { if (pinned) close(); else setPinned(true); }}
                onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
            >
                {children}
            </button>
            <span role="tooltip" id={id} hidden={!open} style={TIP_STYLE}>{tip}</span>
        </span>
    );
};

const money = (n) => `$${Number(n).toFixed(2)}`;
const CodeTip = ({ item }) => (
    <>
        <b className="d-block">{item.code}{item.label ? ` — ${item.label}` : ''}</b>
        {item.reason && <span className="d-block">{item.reason}</span>}
        <span className="d-block">Units: {item.units}</span>
        {item.rate != null && <span className="d-block">Rate (estimate): {money(item.rate)}{item.units > 1 ? ` each, ${money(item.rate * item.units)} total` : ''}</span>}
        {item.sourceLabel && <span className="d-block">Rate source: {item.sourceLabel}</span>}
    </>
);

const Codes = ({ row }) => {
    if (!row.items.length) return <small className="text-muted">No code this month</small>;
    return (
        <ul className="list-unstyled mb-0" data-testid="bc-codes">
            {row.items.map((i) => (
                <li key={`${i.code}-${i.units}`} data-testid="bc-code" style={{ lineHeight: 1.6 }}>
                    <Tip tip={<CodeTip item={i} />} triggerStyle={{ borderBottom: '1px dotted currentColor' }}>
                        <b>{i.code}</b> × {i.units}
                    </Tip>
                </li>
            ))}
        </ul>
    );
};

// Blocker chips: short name + what to add and who fixes it (keys from accounting MISSING_LABELS).
export const BLOCKERS = {
    insurance: ['Insurance', 'Add complete, active insurance coverage (patient profile > Insurance).', 'Org Manager'],
    payer: ['Insurance', 'Add the patient\'s payer / insurance company (patient profile > Insurance).', 'Org Manager'],
    insuranceCheck: ['Insurance check', 'Verify the patient\'s insurance coverage.', 'Org Manager / billing team'],
    consent: ['Consent', 'Record the patient\'s signed consent for this program.', 'Care team / enrollment staff'],
    enrollment: ['Not enrolled', 'Sign the patient up for this program.', 'Care team / enrollment staff'],
    carePlan: ['Care plan', 'Add a current care plan (plan of care) for this program.', 'Provider / care team'],
    placeOfService: ['Place of service', 'Set the place of service used for billing this program.', 'Org Manager'],
    visit: ['Initiating visit', 'Record the initiating visit with the billing provider.', 'Provider / office staff'],
    conditions: ['Conditions list', 'Confirm the patient\'s chronic conditions list.', 'Provider'],
    provider: ['Provider', 'Assign the patient\'s billing provider.', 'Org Manager'],
    practice: ['Practice', 'Assign the patient to a practice.', 'Org Manager / Super Admin'],
    emrId: ['EHR ID', 'Add the patient\'s EHR ID.', 'Office staff'],
    billerDecision: ['Billing decision', 'Billing team to decide whether this can be billed.', 'Billing team'],
};
const blockerChips = (row) => {
    const keys = row.missingKeys || [];
    const chips = (keys.length ? keys : row.missing || []).map((k, n) => {
        const b = BLOCKERS[k];
        const fallback = String((row.missing || [])[n] || k);
        return b ? { key: k, label: b[0], what: b[1], who: b[2] } : { key: k, label: fallback.charAt(0).toUpperCase() + fallback.slice(1), what: `Add: ${fallback}.`, who: 'Office staff' };
    });
    const seen = new Set();
    return chips.filter((c) => (seen.has(c.label) ? false : seen.add(c.label)));
};
const CHIP_STYLE = { background: '#fdecea', color: '#a61b1b', border: '1px solid #f5c2c0', borderRadius: 10, padding: '0 7px', fontSize: 11, fontWeight: 600, lineHeight: '18px' };
const Blockers = ({ row }) => {
    const chips = blockerChips(row);
    if (!chips.length) return <small className="text-muted">None</small>;
    return (
        <span className="d-flex flex-wrap" style={{ gap: 4 }}>
            {chips.map((c) => (
                <Tip key={c.key} testId="bc-chip" triggerStyle={CHIP_STYLE} tip={<><b className="d-block">Missing: {c.label}</b><span className="d-block">{c.what}</span><span className="d-block">Who fixes it: {c.who}</span></>}>
                    {c.label}
                </Tip>
            ))}
        </span>
    );
};

export const firstSentence = (text) => {
    const t = String(text || '').trim();
    const m = t.match(/^[\s\S]*?[.!?](?=\s|$)/);
    const first = m ? m[0] : t;
    return { first, more: first.length < t.length };
};
const Why = ({ text }) => {
    const { first, more } = firstSentence(text);
    return (
        <small data-testid="bc-why">
            {first}
            {more && (
                <>
                    {' '}
                    <Tip tip={text} triggerClassName="text-primary" triggerStyle={{ textDecoration: 'underline', cursor: 'pointer' }} ariaLabel="More: full explanation">More</Tip>
                </>
            )}
        </small>
    );
};

const PatientCell = ({ row, role, onPdf }) => (
    <>
        {PROFILE_ROLES.includes(role)
            ? <Link to={{ pathname: '/patientprofile', state: { patientid: row.patientId } }}>{row.patientName}</Link>
            : <b>{row.patientName}</b>}
        <small className="d-block text-muted">{row.emrId ? `EHR ${row.emrId}` : 'No EHR ID'}</small>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onPdf(row)}>Monthly report (PDF)</button>
    </>
);

const saveBlob = (data, name) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

const BillingCodes = () => {
    const { user } = useSelector((state) => state.auth);
    const [year, setYear] = useState(moment().year());
    const [month, setMonth] = useState(moment().month() + 1);
    const [orgs, setOrgs] = useState(null);
    const [orgId, setOrgId] = useState('');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');

    useEffect(() => { fetchMyOrganizations().then((d) => setOrgs(d)); }, []);
    const qs = `year=${year}&month=${month}${orgId ? `&organizationId=${orgId}` : ''}`;
    const load = useCallback(async () => {
        setError(null);
        setLoading(true);
        try { setData((await axios.get(`${Prod01}/accounting/monthly?${qs}`)).data); } catch (e) { setError(errText(e)); setData(null); }
        setLoading(false);
    }, [qs]);
    useEffect(() => { load(); }, [load]);

    const ym = `${year}-${String(month).padStart(2, '0')}`;
    const download = async (format) => {
        setError(null);
        try {
            const res = await axios.get(`${Prod01}/accounting/monthly/export?${qs}&view=codes&format=${format}`, { responseType: 'blob' });
            saveBlob(res.data, `billing-codes-${ym}.${format}`);
        } catch (e) { setError(errText(e)); }
    };
    const openPdf = async (row) => {
        setError(null);
        try {
            const res = await axios.get(`${Prod01}/general/report/monthly/pdf/${row.patientId}`, { params: { month: ym }, responseType: 'blob' });
            saveBlob(res.data, `monthly-report-${ym}.pdf`);
        } catch (e) { setError(errText(e)); }
    };

    const counts = useMemo(() => (data?.rows || []).reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {}), [data]);
    const filteredRows = useMemo(() => (data?.rows || []).filter((r) => filter === 'all' || r.status === filter), [data, filter]);
    const sort = useSortableTable(filteredRows, { id: 'billing.codes', columns: SORT_COLUMNS, defaultSort: { key: 'patient', dir: 'asc' } });
    const orgList = orgs?.organizations || [];

    return (
        <RoleShell title="Billing — Suggested codes">
            <div className="container-fluid mt-3" data-testid="billing-codes">
                <div className="d-flex flex-wrap align-items-center mb-3">
                    <h5 className="mb-0 mr-3">Billing — Suggested codes</h5>
                    <MonthPicker year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
                    {(orgs?.global || orgList.length > 1) && (
                        <select aria-label="Practice" className="form-control form-control-sm ml-3" style={{ width: 240 }} value={orgId} onChange={(e) => setOrgId(e.target.value)}>
                            <option value="">{orgs?.global ? 'All practices' : 'All my practices'}</option>
                            {orgList.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
                        </select>
                    )}
                    <span className="ml-auto">
                        <button type="button" className="btn btn-outline-primary btn-sm mr-2" onClick={() => download('xlsx')} disabled={!data}>Download Excel</button>
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => download('csv')} disabled={!data}>Download CSV</button>
                    </span>
                </div>
                <p className="text-muted" style={{ fontSize: 13 }}>
                    Codes are suggestions from the billing engine for each patient and program this month — not a claim. Check the blockers, bill in your billing system, then record the claim status here.
                </p>
                <div className="btn-group btn-group-sm mb-2" role="group" aria-label="Status filter">
                    {FILTERS.map(([k, l]) => (
                        <button key={k} type="button" className={`btn ${filter === k ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setFilter(k)}>
                            {l}{k === 'all' ? ` (${data?.rows?.length || 0})` : ` (${counts[k] || 0})`}
                        </button>
                    ))}
                </div>
                {error && <div className="alert alert-danger">{error}</div>}
                {loading && !data && <div>Loading…</div>}
                {data && (
                    <table className="table table-sm" style={{ fontSize: 13 }}>
                        <thead>
                            <tr>
                                <SortableTh sortKey="patient" sort={sort}>Patient</SortableTh>
                                <SortableTh sortKey="practice" sort={sort}>Practice</SortableTh>
                                <SortableTh sortKey="program" sort={sort}>Program</SortableTh>
                                <th>Suggested codes (units)</th>
                                <th>Why (plain words)</th>
                                <th>Blockers</th>
                                <SortableTh sortKey="status" sort={sort}>Status</SortableTh>
                                <SortableTh sortKey="claim" sort={sort}>Claim status</SortableTh>
                            </tr>
                        </thead>
                        <tbody>
                            {sort.rows.map((r) => (
                                <tr key={r.key} data-testid="bc-row">
                                    <td><PatientCell row={r} role={user?.role} onPdf={openPdf} /></td>
                                    <td>{r.practice || '—'}<small className="d-block text-muted">{r.provider || 'No billing provider'}</small></td>
                                    <td>{r.programLabel}</td>
                                    <td><Codes row={r} /></td>
                                    <td><Why text={r.explanation} /></td>
                                    <td data-testid="bc-blockers"><Blockers row={r} /></td>
                                    <td><Status row={r} /></td>
                                    <td><ClaimCell row={r} canEdit={!!data.canEditClaims} year={year} month={month} onSaved={load} /></td>
                                </tr>
                            ))}
                            {!sort.rows.length && <tr><td colSpan={8} className="text-muted">No patients for this month and filter.</td></tr>}
                        </tbody>
                    </table>
                )}
                {data?.disclaimer && <small className="text-muted d-block">{data.disclaimer}</small>}
            </div>
        </RoleShell>
    );
};

export default BillingCodes;
